import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapseBlock } from '../../../../common/index.jsx';
import { CellLayout } from '../../../../common/index.jsx';
import { isUndefined } from 'lodash';
import { formattedFullName } from '../../../../../utils/helpers/CommonHelper.jsx';
import ProtectedEmailLink from '../../../../common/ProtectedEmailLink/ProtectedEmailLink.jsx';
import {
  ROLE_DEPARTMENT_DEPUTY,
  ROLE_DEPARTMENT_HEAD,
  ROLE_DEPARTMENT_SECRETARY,
} from '../../../../../utils/constants/roles.js';

export default function WorkplaceGeneralInformation({ department }) {
  const { t } = useTranslation();
  const isKatedra = String(department?.title_sk ?? '').trim().startsWith('Katedra');

  const getEmployee = useCallback(
    (position) => department.mapped_functions[position] ?? null,
    [department],
  );

  const headLabel = isKatedra ? 'Vedúci katedry' : 'Vedúci pracoviska';
  const deputyLabel = isKatedra
    ? 'Zástupca vedúceho katedry'
    : 'Zástupca vedúceho pracoviska';

  const hasValue = (value) => {
    return !isUndefined(value) && value !== null && String(value).trim() !== '';
  };

  const hasGeneralInformation =
    hasValue(department?.website) ||
    hasValue(department?.phone) ||
    (getEmployee(ROLE_DEPARTMENT_HEAD) || []).length > 0 ||
    (getEmployee(ROLE_DEPARTMENT_DEPUTY) || []).length > 0 ||
    (getEmployee(ROLE_DEPARTMENT_SECRETARY) || []).length > 0;

  if (!hasGeneralInformation) {
    return null;
  }

  return (
    <CollapseBlock title={t('Všeobecné informácie')} collapsible>
      {(hasValue(department?.website)) ? (
        <CellLayout layout="two-cells">
          <CellLayout.Cell as="strong">{t('Webová adresa')}</CellLayout.Cell>
          <CellLayout.Cell className="break-all">
            <a href={department?.website}>{department?.website}</a>
          </CellLayout.Cell>
        </CellLayout>
      ) : null}
      {(hasValue(department?.phone)) ? (
        <CellLayout layout="two-cells">
          <CellLayout.Cell>{t('Telefónne číslo')}</CellLayout.Cell>
          <CellLayout.Cell>{department?.phone}</CellLayout.Cell>
        </CellLayout>
      ) : null}

      <Table
        modelArray={getEmployee(ROLE_DEPARTMENT_HEAD)}
        position={t(headLabel)}
      />
      <Table
        modelArray={getEmployee(ROLE_DEPARTMENT_DEPUTY)}
        position={t(deputyLabel)}
      />
      <Table
        modelArray={getEmployee(ROLE_DEPARTMENT_SECRETARY)}
        position={t('Sekretariát')}
      />
    </CollapseBlock>
  );
}

function Table({ modelArray, position }) {
  const { t } = useTranslation();

  return (modelArray || []).map((model, index) => (
    <div
      className="mt-10px"
      key={model.id ?? `${model.role ?? position}-${model.employee?.person?.id ?? model.employee?.person?.person_id ?? 'unknown'}-${index}`}
    >
      <CellLayout layout="two-cells">
        <CellLayout.Cell as="strong">{position}</CellLayout.Cell>
        <CellLayout.Cell>
          {formattedFullName(model.employee.person)}&nbsp;
          <ProtectedEmailLink
            user={model.employee.person.u}
            domain={model.employee.person.d}
          />
        </CellLayout.Cell>
      </CellLayout>
      <CellLayout layout="two-cells">
        <CellLayout.Cell>{t('Telefónne číslo')}</CellLayout.Cell>
        <CellLayout.Cell>{model.employee.phone}</CellLayout.Cell>
      </CellLayout>
      <CellLayout layout="two-cells">
        <CellLayout.Cell>{t('Miestnosť')}</CellLayout.Cell>
        <CellLayout.Cell>{model.employee.room}</CellLayout.Cell>
      </CellLayout>
    </div>
  ));
}
